import classNames from 'classnames';
import { isEmpty, omit } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import ColorwayPicker from 'components/ColorwayPicker';
import HoverCard from 'components/HoverCard';
import { LargeUp, Medium, Small } from 'components/MediaQuery';
import {
  COLLECTION_COLORWAY_PICKER_APPEARANCES,
  COLLECTION_COLORWAY_PICKER_ITEM_TYPES,
} from 'constants/collectionPage';
import { medium, largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

import Colorway, { COLORWAY_SHAPE } from './Colorway';
import DesktopSizeSelector from './DesktopSizeSelector';
import MobileSizeSelector from './MobileSizeSelector';

const MasterProductCard = class extends React.Component {
  constructor() {
    super();
    this.state = {
      isOpen: false,
    };
  }

  openSizeSelector = () => this.setState({ isOpen: true });

  closeSizeSelector = () => this.setState({ isOpen: false });

  toggleSizeSelector = () =>
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
    }));

  handleEnterKeyUp = e => {
    if (e.key === 'Enter') {
      this.toggleSizeSelector();
    }
  };

  render() {
    const {
      currentColorway,
      onSizeSelection,
      trackNavigation,
      enableDiscountPriceColor,
      hasMobileQuickAddRow,
    } = this.props;
    const { isOpen } = this.state;

    return (
      <div
        className={classNames('MasterProductCard', [
          {
            'MasterProductCard--sizes': isEmpty(currentColorway.sortedSizes),
          },
        ])}
      >
        <LargeUp>
          <HoverCard
            top={Theme.get('spacing') * 4}
            right={Theme.get('spacing') * 4}
            bottom={Theme.get('spacing') * 4}
            left={Theme.get('spacing') * 4}
            onKeyUp={this.handleEnterKeyUp}
            onBlur={this.closeSizeSelector}
            onMouseEnter={this.openSizeSelector}
            onMouseLeave={this.closeSizeSelector}
            aria-expanded={isOpen}
          >
            <Colorway
              {...omit(currentColorway, 'colorName')}
              isDesktop
              trackNavigation={trackNavigation}
              enableDiscountPriceColor={enableDiscountPriceColor}
            />
            {/* The key prop below is a mechanism for gracefully refreshing the carousel when the content of the slides change */}
            <ColorwayPicker
              {...this.props}
              slides={this.props.colorwayPickerSlides}
              activeColorwayHandle={currentColorway.handle}
              key={this.props.colorwayPickerSlides.length}
            />
            {isOpen && !isEmpty(currentColorway.sortedSizes) && (
              <DesktopSizeSelector
                sortedSizes={currentColorway.sortedSizes}
                onSizeSelection={onSizeSelection}
                type={currentColorway.type}
                isOpen={isOpen}
              />
            )}
          </HoverCard>
        </LargeUp>
        <Medium>
          <Colorway
            {...omit(currentColorway, 'colorName')}
            isDesktop={false}
            trackNavigation={trackNavigation}
            enableDiscountPriceColor={enableDiscountPriceColor}
          />
          {/* The key prop below is a mechanism for gracefully refreshing the carousel when the content of the slides change */}
          <ColorwayPicker
            {...this.props}
            slides={this.props.colorwayPickerSlides}
            activeColorwayHandle={currentColorway.handle}
            key={this.props.colorwayPickerSlides.length}
          />
          {!isEmpty(currentColorway.sortedSizes) && !hasMobileQuickAddRow ? (
            <MobileSizeSelector
              sortedSizes={currentColorway.sortedSizes}
              onSizeSelection={onSizeSelection}
              type={currentColorway.type}
              isOpen={isOpen}
            />
          ) : null}
        </Medium>
        <Small>
          <Colorway
            {...currentColorway}
            isDesktop={false}
            trackNavigation={trackNavigation}
            enableDiscountPriceColor={enableDiscountPriceColor}
          />

          {/* The key prop below is a mechanism for gracefully refreshing the carousel when the content of the slides change */}
          <ColorwayPicker
            {...this.props}
            slides={this.props.colorwayPickerSlides}
            activeColorwayHandle={currentColorway.handle}
            key={this.props.colorwayPickerSlides.length}
          />

          {!isEmpty(currentColorway.sortedSizes) && !hasMobileQuickAddRow ? (
            <MobileSizeSelector
              sortedSizes={currentColorway.sortedSizes}
              onSizeSelection={onSizeSelection}
              type={currentColorway.type}
              isOpen={isOpen}
            />
          ) : null}
        </Small>
        <style jsx>
          {`
            .MasterProductCard {
              display: flex;
              flex-direction: column;
              width: 100%;
              height: 100%;
              position: relative;
              background-color: ${Theme.get('colors-white')};
            }

            .MasterProductCard.MasterProductCard--v2 {
              display: flex;
              flex-direction: column;
            }
          `}
        </style>
        <style jsx global>
          {`
            .MasterProductCard * {
              touch-action: manipulation;
            }

            .MasterProductCard .ShadowBox {
              height: 100%;
              display: flex;
              flex-direction: column;
            }

            .MasterProductCard .ColorwayPicker--color-swatches {
              padding: 0 ${Theme.get('spacing') * 1}px ${Theme.get('spacing') * 1}px;
              max-width: ${Theme.get('spacing') * 69}px;
            }

            .MasterProductCard--v2 .ColorwayPicker--color-swatches {
              padding: 0 ${Theme.get('spacing') * 3}px;
              padding-bottom: ${Theme.get('spacing') * 2}px;
            }

            .MasterProductCard--v2 .ColorwayPicker--color-swatches {
              padding: 0 ${Theme.get('spacing') * 3}px;
              padding-bottom: ${Theme.get('spacing') * 2}px;
            }

            .MasterProductCard--v2 .MobileSizeSelector__grid-item {
              width: ${100 / 3}%;
            }

            .MasterProductCard--v2 .MobileSizeSelector__grid-item--3-column {
              width: 50%;
              flex-grow: 1;
            }

            .MasterProductCard .ColorwayPicker--thumbnails {
              margin: 0 ${Theme.get('spacing') * -0.5}px;
            }

            @media ${medium} {
              .MasterProductCard .ColorwayPicker--thumbnails {
                padding: ${Theme.get('spacing') * 0.5}px ${Theme.get('spacing') * 2}px
                  ${Theme.get('spacing') * 2}px ${Theme.get('spacing') * 3}px;
              }

              .MasterProductCard .ColorwayPicker--color-swatches {
                padding: 0 ${Theme.get('spacing') * 3}px;
              }
            }

            @media ${largeUp} {
              .MasterProductCard .ColorwayPicker--color-swatches {
                padding: 0;
              }
            }

            .MasterProductCard .ShadowBox .SwatchList {
              padding-left: ${Theme.get('spacing') * 2}px;
              padding-right: ${Theme.get('spacing') * 3}px;
            }

            .MasterProductCard .HoverCard {
              height: 100%;
            }

            .MasterProductCard .HoverCard .HoverCard__children {
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }

            .MasterProductCard .HoverCard .SwatchList {
              margin: ${Theme.get('spacing') * -0.5}px 0 ${Theme.get('spacing') * -1}px
                ${Theme.get('spacing') * -1}px;
            }

            .MasterProductCard.MasterProductCard--sizes .ShadowBox {
              padding-bottom: ${Theme.get('spacing') * 2}px;
            }

            .MasterProductCard .ShadowBox .SwatchList {
              padding-left: ${Theme.get('spacing') * 3}px;
              padding-right: ${Theme.get('spacing') * 3}px;
            }
          `}
        </style>
      </div>
    );
  }
};

MasterProductCard.propTypes = {
  currentColorway: PropTypes.shape(COLORWAY_SHAPE).isRequired,
  colorwayPickerSlides: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.values(COLLECTION_COLORWAY_PICKER_ITEM_TYPES)).isRequired,
        data: PropTypes.object,
      })
    )
  ).isRequired,
  onSwatchSelect: PropTypes.func,
  appearance: PropTypes.oneOf(Object.values(COLLECTION_COLORWAY_PICKER_APPEARANCES)).isRequired,
  onSizeSelection: PropTypes.func.isRequired,
  type: PropTypes.string,
  currentSlideIndex: PropTypes.number,
  enableDiscountPriceColor: PropTypes.bool,
  nItemsPerSlide: PropTypes.number.isRequired,
  onSlideIndexIncrement: PropTypes.func,
  onSlideIndexDecrement: PropTypes.func,
  trackNavigation: PropTypes.func,
  isV2: PropTypes.bool,
  v2ShowQuickAdd: PropTypes.bool,
  hasMobileQuickAddRow: PropTypes.bool,
};

MasterProductCard.defaultProps = {
  onSwatchSelect: () => {},
  enableDiscountPriceColor: false,
  currentSlideIndex: 0,
  onSlideIndexIncrement: () => {},
  onSlideIndexDecrement: () => {},
  trackNavigation: () => {},
  isV2: false,
  type: null,
  v2ShowQuickAdd: false,
};

export default MasterProductCard;
